import type { Risk, Vulnerability } from '@rtkwlf/mr-infra-risk';
import { useTranslation } from 'react-i18next';

import {
  DrawerType,
  useActionDrawer,
} from '@packages/components/action-drawer/action-drawer-context';
import { Typography, LinkRouter, Button } from '@packages/fenrir/components';
import { Stack } from '@packages/fenrir/components/stack';

import { CVEList } from '../cve-list/cve-list';

interface CVELinksWithDrawerProps {
  item: Risk | Vulnerability;
  maxVisibleCVEs?: number;
}

export const CVELinksWithDrawer = ({
  item,
  maxVisibleCVEs = 1,
}: CVELinksWithDrawerProps) => {
  const { openDrawer } = useActionDrawer();
  const { t } = useTranslation('riskManagement', { keyPrefix: 'risks' });

  const cveIds = item.cve?.primary
    ? [item.cve.primary.id, ...(item.cve.additional ?? [])]
    : [];

  if (cveIds.length === 0) {
    return null;
  }

  const visibleCVEs = cveIds.slice(0, maxVisibleCVEs);
  const remainingCount = Math.max(0, cveIds.length - maxVisibleCVEs);

  return (
    <Stack justifyContent="center" spacing={1}>
      {visibleCVEs.map((cveId) => (
        <LinkRouter
          key={cveId}
          to={`https://nvd.nist.gov/vuln/detail/${cveId}`}
          isExternal={true}
        >
          <Typography variant="body2">{cveId}</Typography>
        </LinkRouter>
      ))}

      {remainingCount > 0 && (
        <Typography
          variant="body2"
          component={Button}
          sx={{
            cursor: 'pointer',
            color: 'primary.main',
            p: 0,
          }}
          onClick={() =>
            openDrawer(<CVEList item={item} />, {
              drawerType: DrawerType.FULL_HEIGHT_DRAWER,
            })
          }
        >
          {t('viewMore', { count: remainingCount })}
        </Typography>
      )}
    </Stack>
  );
};
